import './item.less';
import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classname';

import find from 'lodash/find';
import findKey from 'lodash/findKey';
import isObject from 'lodash/isObject';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isNull from 'lodash/isNull';

import {
	getNonTerminalItemText,
	getConversionPayoutReviewItemText,
	getTimelineItemAvatar,
	getPayoutItemText,
	getEmailEventText,
	getThreadItemText
} from './entities';

import { getCurrencyIcon } from '../../../../../../../shared/js/get-currency-icon';
import { formatMoney } from '../../../../shared/format';
import Avatar from '../../../../shared/avatar';

import affiliateLevels from '../../../../../../../../enums/affiliate-levels';
import affiliateStatuses from '../../../../../../../../enums/affiliate-statuses';

const isRocket = process.env.CPA_ENV === 'rocket';

const Main = ({ data, item, index, logs, newsTypesList, notificationEventTypesList, intl }) => {
	const content = getTimelineItemContent(item, newsTypesList, data, intl);
	const avatar = getTimelineItemAvatar(item);
	return (
		<div
			className={cn({
				'timeline-item': true,
				'timeline-item-border':
					!content.thread &&
					!content.comment &&
					!content.campaign &&
					!content.emailTrackData &&
					!content.emailPayout &&
					!content.emailDigest &&
					!content.emailThread &&
					!content.emailDailyNews &&
					!content.fields &&
					!content.indTerms &&
					!content.point_transactions &&
					index !== logs.rows.length - 1
			})}
			key={index}
		>
			<Avatar className="avatar" src={avatar} />
			<div className="timeline-item-head">
				<span className="text">
					<span>
						<span className="black">{content.email}</span> {content.action}
						{content.entity ? content.entity : null}
					</span>
				</span>
				<span className="date">{item.date}</span>
			</div>
			{content.thread && (
				<div className="timeline-item-block">
					<a href={`threads/${content.thread.id}`} target="_blank">
						{content.thread.title}
					</a>
					<span>{content.thread.text}</span>
				</div>
			)}
			{content.comment && (
				<div className="timeline-item-block">
					<span className='comment-container'>{content.comment}</span>
				</div>
			)}
			{content.campaign && (
				<div className="timeline-item-block campaign">
					<span>
						<span className="black inline-block">
							{content.campaign.title}
							{` (${content.campaign.id}) - `}
							{content.campaign.offer}
						</span>
					</span>
					{content.campaign.transit_domain && (
						<span>
							<a className="inline campaign-link" href={`http://${content.campaign.transit_domain}`} target="_blank">
								{content.campaign.transit_domain}
							</a>
						</span>
					)}
					{content.campaign.landing_domain && (
						<span>
							<a className="inline campaign-link" href={`http://${content.campaign.landing_domain}`} target="_blank">
								{content.campaign.landing_domain}
							</a>
						</span>
					)}
				</div>
			)}
			{content.emailPayout && (
				<div className="timeline-item-block">
					<span className="black email-info-title">{intl('Payout info')}</span>
					<span>
						<span className="inline">{intl('Payout system: ')}</span>
						<span className="black inline">{content.emailPayout.payout_system_title}</span>
					</span>
					<span>
						<span className="inline">{intl('Amount: ')}</span>
						<span className="black inline">
							{formatMoney(content.meta.amount, true)} {getCurrencyIcon(content.emailPayout.currency)}
						</span>
					</span>
					<span>
						<span className="inline">{intl('Status: ')}</span>
						<span className="black inline">{content.meta.status}</span>
					</span>
				</div>
			)}
			{content.fields && (
				<div className="timeline-item-block fields">
					<table>
						<tbody>
							<tr>
								{!['payout_credentials', 'payout_credentials_confirmed'].includes(content.type) && (
									<th>{intl('Field')}</th>
								)}
								{!content.areAllOldFieldsEmpty ? <th>{intl('Old')}</th> : null}
								<th>{intl('New')}</th>
							</tr>
							{Object.keys(content.fields).map((field, i) => (
								<tr key={i}>
									{!['payout_credentials', 'payout_credentials_confirmed'].includes(content.type) ? (
										<td>
											{getFieldName(field, content, { notificationEventTypesList })}
										</td>
									) : null}
									{!content.areAllOldFieldsEmpty ? (
										<td>
											{getFieldValue(content.fields[field]['old'], field, content)}
										</td>
									) : null}
									<td>
										{getFieldValue(content.fields[field]['new'], field, content)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{content.indTerms && (
				<div className="timeline-item-block fields">
					<table>
						<tbody>
							<tr>
								<th>{intl('Geo')}</th>
								<th>{intl('Old')}</th>
								<th>{intl('New')}</th>
							</tr>
							{content.indTerms.map((term, i) => (
								<tr key={i}>
									<td>{term.location}</td>
									<td>{term.amount.old}</td>
									<td>{term.amount.new}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			{content.emailDigest && (
				<div className="timeline-item-block">
					<span className="black inline link">
						{content.emailDigest.title}
					</span>
				</div>
			)}

			{content.emailDailyNews && (
				<div className="timeline-item-block">
					<span className="black email-info-title">{intl('News')}</span>
					{content.emailDailyNews.map(news => (
						<span className="link" key={news.id}>
							<a href={`news/edit/${news.id}`} target="_blank">
								{news.title}
							</a>
						</span>
					))}
				</div>
			)}
		</div>
	);
};

const getTimelineItemContent = (item, newsTypesList, user, intl) => {
	let result = {};
	switch (item.action) {
		case 'new_affiliate':
			result = {
				email: item.affiliate_email,
				action: intl('signed up')
			};
			break;
		case 'login':
			result = {
				email: item.affiliate_email,
				action: intl(
					'tried to login from ip %s, user agent %s, referer %s (%s)',
					item.metadata.ip || 'local',
					item.metadata.user_agent,
					item.metadata.referer,
					item.metadata.status
				)
			};
			break;
		case 'listen_record':
			result = {
				email: item.metadata.email,
				action: intl('listened to record of conversion '),
				entity: (
					<span className="black">#{item.metadata.conversion_id}</span>
				)
			};
			break;
		case 'non-terminal-status-change':
			result = {
				email: intl('Non terminal status was changed.'),
				action: `Offer #${item.offer_id}, Conversion #${item.metadata.conversion_id}.`
			};
			result = getNonTerminalItemText(item, intl);
			break;
		case 'conversion_payout_review':
			result = getConversionPayoutReviewItemText(item, intl);
			break;
		case 'added_offer':
			result = {
				entity: (
					<a href={`programs/editor/${item.offer_id}`} target="_blank" className="black">
						{' '}
						{item.offer}
					</a>
				)
			};
			result.email = item.admin_id
				? item.admin_email
				: item.master_account ? item.master_account.email : item.affiliate_email;
			result.action = item.admin_id
				? intl('gave access to offer')
				: item.master_account ? intl('(master) gave access to offer') : intl('added offer');
			break;
		case 'revoked_offer':
			result = {
				entity: (
					<a href={`programs/editor/${item.offer_id}`} target="_blank" className="black">
						{' '}
						{item.offer}
					</a>
				)
			};
			result.email = item.admin_email;
			result.action = intl('revoked access to offer');
			break;
		case 'payout_status_change':
			result = getPayoutItemText(item, intl);
			break;
		case 'new_thread':
			result = getThreadItemText(item, intl);
			break;
		case 'closed_thread':
			result = {
				...getThreadItemText(item, intl),
				action: intl(isCloseAutomatically(item) ? 'system closed ticket automatically' : 'closed ticket'),
				email: item.thread_action_user && item.thread_action_user.email
			};
			break;
		case 'paused_thread':
			result = {
				...getThreadItemText(item, intl),
				action: intl('paused ticket'),
				email: item.thread_action_user && item.thread_action_user.email
			};
			break;
		case 'unpaused_thread':
			result = {
				...getThreadItemText(item, intl),
				action: intl('unpaused ticket'),
				email: item.thread_action_user && item.thread_action_user.email
			};
			break;
		case 'created_campaign':
			result = {
				email: item.affiliate_email,
				action: intl('created campaign'),
				campaign: {
					...item.campaign,
					offer: (
						<a href={`programs/editor/${item.offer_id}`} target="_blank" className="black inline campaign-link">
							{item.offer}
						</a>
					)
				}
			};
			break;
		case 'admin_transaction':
			result = {
				email: item.admin_email,
				action: item.admin_transaction.type_id === 24
					? intl('transferred with hidden transaction')
					: intl('transferred'),
				entity: (
					<span className="black">
						{' '}
						{formatMoney(item.admin_transaction.amount, true)} {getCurrencyIcon(item.admin_transaction.currency)}
						&nbsp;({item.admin_transaction.comment})
					</span>
				)
			};
			break;
		case 'admin_transfer_money':
			result = {
				email: item.admin_email,
				action: intl('transferred'),
				entity: (
					<div className="timeline-item-block">
						<p className="black">
							&nbsp;Amount: {'  '}
							{formatMoney(item.metadata.amount, true)} {getCurrencyIcon(item.metadata.currency)}
						</p>
						<p>&nbsp;Comment: {item.comment}</p>
						<p>&nbsp;Countries: {item.metadata.geos.join(',')}</p>
						<p>&nbsp;Offers: {item.metadata.offers.join(',')}</p>
						<p>&nbsp;Conversions: {item.metadata.conversion_ids.join(', ')}</p>
					</div>
				)
			};
			break;
		case 'move_to_hold':
			result = {
				email: item.admin_email,
				action: intl('transferred'),
				entity: (
					<span className="black">
						{' '}
						{formatMoney(item.move_to_hold.hold_amount, true)}
						{getCurrencyIcon(item.move_to_hold.currency)} {intl('from balance to hold')}
					</span>
				)
			};
			break;
		case 'move_to_balance':
			result = {
				email: item.admin_email,
				action: intl('transferred'),
				entity: (
					<span className="black">
						{' '}
						{formatMoney(item.move_to_balance.amount, true)}
						{getCurrencyIcon(item.move_to_balance.currency)} {intl('from hold to balance')}
					</span>
				)
			};
			break;
		case 'order_bm_account':
			result = {
				action: `${item.affiliate_email} ${intl('ordered bm account')} "${item.metadata.name}"`,
				entity: (
					<div className="black">
						{' '}
						{formatMoney(item.metadata.amount, true)}
						{getCurrencyIcon(item.metadata.currency)} {intl('to balance')}
					</div>
				)
			};
			break;
		case 'manual_point_transaction':
			result = {
				email: item.admin_email,
				action: intl('transferred'),
				comment: item.point_transactions.comment,
				entity: (
					<span className="black">
						{' '}
						{formatMoney(item.point_transactions.amount, true)} points
					</span>
				)
			};
			break;
		case 'admin_comment':
			result = {
				email: item.admin_email,
				action: intl('left comment'),
				comment: item.comment
			};
			break;
		case 'new_manual_guarant':
			result = {
				email: item.admin_email,
				action: intl('Increase approve'),
				entity: (
					<div className="timeline-item-block">
						<p>Countries: {item.metadata.countries.join(', ')}</p>
						<p>Conversion ids: {item.metadata.conversion_ids.join(', ')}</p>
						<p>Approval percent before: {item.metadata.approval_percent} %</p>
						<p>Approval percent after: {item.metadata.approve_after_increasing} %</p>
						<p>
							Added to balance:
							{
								item.metadata && item.metadata.conversion_payouts &&
								Object.keys(item.metadata.conversion_payouts)
									.filter(currency => item.metadata.conversion_payouts[currency] > 0)
									.map(currency => `${item.metadata.conversion_payouts[currency]} ${currency.toUpperCase()}`)
									.join(';')
							}
						</p>
						{
							item.metadata.date_range &&
							<p>Period: {item.metadata.date_range.start} - {item.metadata.date_range.end}</p>
						}
						<p>Offer: {item.offer}</p>
					</div>
				)
			};
			break;
		case 'campaign_payout_change':
			result = {
				email: item.admin_email,
				action: intl('changed campaign payouts'),
				comment: item.metadata.difference &&
					item.metadata.difference.join(' \n')
			};
			break;
		case 'opened_email':
		case 'clicked_email':
		case 'unsubscribed_email':
		case 'complained_email':
		case 'dropped_email':
		case 'bounced_email':
		case 'failed_email':
			result = {
				...getEmailEventText(item, newsTypesList, intl),
				email: item.affiliate_email
			};
			break;
		case 'affiliate_fields_change':
			result = {
				email: item.metadata.is_system_change ? intl('system') : item.admin_email || item.affiliate_email,
				fields: item.metadata.fields && { ...item.metadata.fields },
				areAllOldFieldsEmpty: areAllOldFieldsEmpty(item.metadata.fields),
				type: item.metadata.type
			};

			if (result.fields) {
				result.action = result.areAllOldFieldsEmpty ? intl('added fields ') : intl('changed fields ');

				if (result.fields.manager_id && item.changed_managers) {
					result.fields.manager_id = { ...item.changed_managers };
				}
				if (result.fields.geos && item.geos) {
					result.fields.geos = { ...item.geos };
				}

				//Booleans
				[
					'is_master',
					'is_suspicious',
					'is_two_factor_authentication_enabled',
					'is_pseudotrash_enabled',
					'is_top'
				].forEach((prop) => {
					if (result.fields[prop]) {
						result.fields[prop] = {
							new: JSON.stringify(result.fields[prop].new),
							old: JSON.stringify(result.fields[prop].old)
						};
					}
				});

				if (result.fields.is_two_factor_authentication_enabled) {
					result.fields.is_two_factor_authentication_enabled = {
						new: JSON.stringify(result.fields.is_two_factor_authentication_enabled.new),
						old: JSON.stringify(result.fields.is_two_factor_authentication_enabled.old)
					};
				}
			}

			if (item.metadata.type === 'notification_settings') {
				result.action = intl('changed notification settings');
			}

			if (item.metadata.type === 'payout_credentials') {
				result.action = result.areAllOldFieldsEmpty
					? intl('added payout credentials for ')
					: intl('changed payout credentials for ');
				result.entity = item.metadata.payout_system && (
					<a href={`payout-systems/edit/${item.metadata.payout_system.id}`} target="_blank" className="black">
						{' '}
						{item.metadata.payout_system.title}
					</a>
				);
			}
			if (item.metadata.type === 'payout_credentials_confirmed') {
				result.action = intl('confirmed payout credentials for ');
				result.entity = item.metadata.payout_system && (
					<a href={`payout-systems/edit/${item.metadata.payout_system.id}`} target="_blank" className="black">
						{item.metadata.payout_system.title}
					</a>
				);
			}
			break;
		case 'affiliate_ind_terms_change':
			result = {
				email: item.master_account ? item.master_account.email : item.admin_email || item.affiliate_email,
				action: '',
				entity: (
					<div>
						<a href={`programs/editor/${item.offer_id}`} target="_blank" className="black">
							{' '}
							{item.offer}
						</a>
						{!!item.metadata.campaign_id && <p>Campaign id: {item.metadata.campaign_id}</p>}
						{!!item.metadata.sid_type && <p>Sid type: {item.metadata.sid_type}</p>}
						{!!item.metadata.sid && <p>Sid: {item.metadata.sid}</p>}
					</div>
				),
				indTerms: []
			};
			switch (true) {
				case item.metadata.type === 'affiliate_boost':
					result.action = intl('used Boost for ');
					break;
				case item.metadata.type === 'delete_terms':
					result.action = intl('deleted individual terms on offer');
					break;
				case !!item.master_account && item.metadata.type === 'copy_terms':
					result.action = intl('(master) copied individual terms on offer');
					break;
				case !!item.master_account:
					result.action = intl('(master) changed individual terms on offer');
					break;
				case item.metadata.type === 'change_terms' && isNull(item.admin_id):
					result.email = null;
					result.action = intl('system changed individual terms on offer');
					break;
				case item.metadata.type === 'change_terms' && !isNull(item.admin_id):
					result.action = intl('changed individual terms on offer');
					break;
				default:
					result.action = intl('changed individual terms on offer');
					break;
			}
			populateIndTermsLocations(item, result);
			break;
		case 'deleted_child_affiliate':
			result = {
				email: item.admin_email,
				action: user.is_master ? intl('deleted child affiliate') : intl('deleted this affiliate from master'),
				entity: (
					<a
						href={`affiliates/profile/${
							user.is_master ? item.child_affiliate && item.child_affiliate.id : item.affiliate_id
						}`}
						target="_blank"
						className="black"
					>
						{' '}
						{user.is_master ? item.child_affiliate && item.child_affiliate.email : item.affiliate_email}
					</a>
				)
			};
			break;
		case 'campaign_edit':
			result = {
				email: item.admin_email,
				action: 'Affiliate edited campaign',
				entity: !!item.metadata &&
					<div>
						{Object.keys(item.metadata).map((key, index) => {
							if (key === 'newDomains') {
								return <p key={index}>
									New domains: {item.metadata[key].map(item => `${item.domain}`).join(', ')}
								</p>;
							}
							if (key === 'removed_at') {
								return <p key={index}> Campaign was removed </p>;
							}
							if (key === 'removedDomains') {
								return <p key={index}>
									Removed domains: {item.metadata[key].map(item => `${item.domain}`).join(', ')}
								</p>;
							}
							if (key === 'campaign_id') {
								return <p key={index}>Campaign id: {item.metadata[key]}</p>;
							}
							return <p key={index}>
								{key}: before = {`${item.metadata[key].before}`} after = {`${item.metadata[key].after}`}
							</p>;

						})}
					</div>
			};
			break;
		case 'added_child_affiliate':
			result = {
				email: item.admin_email,
				action: user.is_master ? intl('added child affiliate') : intl('added this affiliate to master'),
				entity: (
					<a
						href={`affiliates/profile/${
							user.is_master ? item.child_affiliate && item.child_affiliate.id : item.affiliate_id
						}`}
						target="_blank"
						className="black"
					>
						{' '}
						{user.is_master ? item.child_affiliate && item.child_affiliate.email : item.affiliate_email}
					</a>
				)
			};
			break;
		case 'sessions_delete':
			result = {
				email: item.admin_email,
				action: intl('Sessions deleted. Count: %s',
					item.metadata && item.metadata.count)
			};
			break;
		case	'updated_global_postback':
			result = {
				email: item.admin_email,
				action: intl('affiliate updated global postback'),
				entity: !!item.metadata &&
					<div>
						{Object.keys(item.metadata).map((key, index) => {
							if (key === 'global_postback_config' && item.metadata[key]) {
								return <p key={index}>
									Global postback config:
									{Object.keys(item.metadata[key]).map((keyConfig, indexConfig) => {
										return <p key={`${index}-${indexConfig}`}>
											- {keyConfig}: "{JSON.stringify(item.metadata[key][keyConfig])}"
										</p>;
									})}
								</p>;
							}
							return <p key={index}>
								{key}:
								{`"${JSON.stringify(item.metadata[key])}"`}
							</p>;

						})}
					</div>
			};
			break;
		default:
			break;
	}
	return result;
};

Main.propTypes = {
	item: PropTypes.object,
	index: PropTypes.number,
	logs: PropTypes.object,
	newsTypesList: PropTypes.array,
	notificationEventTypesList: PropTypes.array,
	showMoreLogs: PropTypes.func,
	intl: PropTypes.func,
	data: PropTypes.object
};

export default Main;

function areAllOldFieldsEmpty(fieldsObj) {
	return (
		Object.keys(fieldsObj).length &&
		Object.keys(fieldsObj)
			.map(fName => isEmpty(fieldsObj[fName].old))
			.filter(val => !val).length === 0
	);
}

const populateIndTermsLocations = (item, result) => {
	if (item.metadata.fields && Object.keys(item.metadata.fields).length) {
		Object.keys(item.metadata.fields).forEach(locationId => {
			let field = item.metadata.fields[locationId];
			let location = item.ind_terms_locations && find(item.ind_terms_locations, { id: parseInt(locationId) });

			field.forEach(termPerCurrency => {
				result.indTerms.push({
					location: (location && location.text) || locationId,
					amount: {
						new: termPerCurrency.amount.new
							? `${termPerCurrency.amount.new} ${getCurrencyIcon(termPerCurrency.amount.currency)}`
							: null,
						old: termPerCurrency.amount.old
							? `${termPerCurrency.amount.old} ${getCurrencyIcon(termPerCurrency.amount.currency)}`
							: null
					}
				});
			});
		});
	}
};

const isCloseAutomatically = item => !isNull(item.thread.close_message) && isNull(item.thread.closed_admin_id);

const getFieldName = (field, content, { notificationEventTypesList }) => {
	if (content.type === 'notification_settings' && find(notificationEventTypesList, { id: field })) {
		return find(notificationEventTypesList, { id: field }).text;
	}

	if (isRocket && content.type === 'general_fields') {
		if (field === 'status') {
			return 'Confirmation status';
		}
		if (field === 'level') {
			return 'Account status';
		}
	}

	return field;
};

const getFieldValue = (value, field, content) => {
	return isObject(value)
		? isArray(value)
			? value.join(', ')
			: JSON.stringify(value)
		: getValueAlias(value);

	function getValueAlias(value) {
		if (isRocket && content.type === 'general_fields') {
			if (field === 'status') {
				return affiliateStatuses.locale.en[findKey(affiliateStatuses.enum, s => s === value)];
			}
			if (field === 'level') {
				return affiliateLevels.locale.en[findKey(affiliateLevels.enum, l => l === value)];
			}
		}

		return value;
	}
};


// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/affiliates/components/profile/timeline/item.js